import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './CourseCard.css';
import useCourseTestimonials from '../hooks/useCourseTestimonials';
import TestimonialsModal from './TestimonialsModal';
import styles from './CourseCardSnippet.module.css';
import { formatTestimonialSnippet } from '../utils/formatters';
import CourseImageFill from './CourseImageFill';
import { isCourseAvailableForPurchase, isCourseComingSoon } from '../utils/coursePurchaseState';
import { buildCourseDescriptionSnippet } from '../utils/courseDescriptionSnippet';
import { upsertCartCourse } from '../utils/cartCourseSelection';
import { useOwnedCourseIds } from '../hooks/useOwnedCourseIds';
import { isCourseOwnedByUser } from '../utils/ownedCourseIds';
import { getMessage } from '../data/locales';

const CourseCard = ({ course }) => {
  const navigate = useNavigate();
  const { ownedCourseIds, ownershipReady } = useOwnedCourseIds();
  const { _id, titulo, precioFinal, imagen, tags, actualizadoEn, descripcion } = course;
  const descriptionSnippet = buildCourseDescriptionSnippet(descripcion);
  const cardRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const hasEmbedded = Array.isArray(course.testimonios) && course.testimonios.length > 0;
  const { snippet, total } = useCourseTestimonials(hasEmbedded ? null : (visible ? _id : null));
  const [embeddedSnippet, setEmbeddedSnippet] = useState(null);
  const [embeddedTotal, setEmbeddedTotal] = useState(0);

  const isUpdated = (() => {
    if (!actualizadoEn) return false;
    const diff = new Date() - new Date(actualizadoEn);
    return diff < 7 * 24 * 60 * 60 * 1000; // 7 days
  })();

  const canPurchase = isCourseAvailableForPurchase(course);
  const comingSoon = isCourseComingSoon(course);
  const alreadyOwned = ownershipReady && isCourseOwnedByUser(_id, ownedCourseIds);

  const handleBuy = (e) => {
    e.stopPropagation();
    if (!ownershipReady) {
      return;
    }
    if (alreadyOwned) {
      navigate(`/usuario/curso/${_id}`);
      return;
    }
    if (!canPurchase) return;
    upsertCartCourse(course);
    navigate('/pago');
  };

  const handleCardClick = () => {
    if (showModal) return;
    navigate(`/curso/${_id}`);
  };

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      });
    }, { threshold: 0.1 });
    const node = cardRef.current;
    if (node) obs.observe(node);
    return () => {
      if (node) obs.unobserve(node);
      obs.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!hasEmbedded) return;
    const first = course.testimonios[0];
    const text = first && typeof first.text === 'string' ? first.text : '';
    const author = first && (first.author || first.autor || '');
    
    const s = first ? formatTestimonialSnippet(text, author) : null;
    
    setEmbeddedSnippet(s);
    setEmbeddedTotal(course.testimonios.length);
  }, [hasEmbedded, course]);

  const effectiveSnippet = hasEmbedded ? embeddedSnippet : snippet;
  const effectiveTotal = hasEmbedded ? embeddedTotal : total;

  const openModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowModal(true);
  };
  const closeModal = () => setShowModal(false);

  return (
    <>
    <div className="course-card" onClick={handleCardClick} ref={cardRef}>
      <div className="course-card-image-container">
        <CourseImageFill
          src={imagen || ''}
          alt={titulo}
          imageClassName="course-card-image"
        />
        {isUpdated && <span className="badge-updated">Actualizado</span>}
      </div>
      <div className="course-card-content">
        <h3 className="course-card-title">{titulo}</h3>
        <div className="course-card-tags">
          {tags && tags.map((tag, idx) => <span key={idx} className="course-tag">{tag}</span>)}
        </div>
        {descriptionSnippet ? (
          <p className="course-card-description">{descriptionSnippet}</p>
        ) : null}
        {visible && effectiveTotal > 0 && effectiveSnippet && (
          <div
            className={styles.snippetContainer}
            onClick={openModal}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                openModal(e);
              }
            }}
            role="button"
            tabIndex={0}
          >
            <p className={styles.snippetText}>{effectiveSnippet.text}</p>
            <div className={styles.snippetMeta}>
              <span className={styles.snippetAuthor}>{effectiveSnippet.authorShort}</span>
            </div>
          </div>
        )}
        {visible && effectiveTotal > 0 && (
          <button type="button" className={styles.badgeTestimonials} onClick={openModal}>
            {effectiveTotal} testimonios
          </button>
        )}
        {visible && effectiveTotal > 0 && (
          <button type="button" className={styles.readButton} onClick={openModal}>
            Leer testimonios
          </button>
        )}
        <div className="course-card-footer">
            <span className="course-price">{precioFinal != null ? `${precioFinal} €` : ""}</span>
            {!ownershipReady ? (
              <button type="button" className="btn-buy" disabled>
                {getMessage("checkout.checkingAccess")}
              </button>
            ) : alreadyOwned ? (
              <button type="button" className="btn-buy btn-buy--owned" onClick={handleBuy}>
                {getMessage("checkout.alreadyOwnedButton")}
              </button>
            ) : comingSoon ? (
              <button type="button" className="btn-buy btn-buy--soon" disabled>
                Próximamente
              </button>
            ) : (
              <button type="button" className="btn-buy" disabled={!canPurchase} onClick={handleBuy}>
                Comprar
              </button>
            )}
        </div>
      </div>
    </div>
    {showModal && (
      <TestimonialsModal cursoId={_id} onClose={closeModal} />
    )}
    </>
  );
};

export default CourseCard;
